import Phaser from 'phaser';

import { GameSizeMetrics } from 'src/games/common/phaser/game/scenes/scaled-base-scene';
import { BlocksMainScene } from '../../scenes/blocks-main-scene';
import { CellViewModel } from './cell-view-model';
//import { DebugContainerVisual } from '../debug/debug-container-visual';
import { combineLatest, Subscription } from 'rxjs';
import { TextResizer } from './text-resizer';

export class CellView extends Phaser.GameObjects.Container {
  public get id() {
    return this.viewModel.id;
  }
  public get row() {
    return this.viewModel.row;
  }
  public get column() {
    return this.viewModel.column;
  }
  public get canDrop() {
    return this.viewModel.canDrop;
  }
  private hadBlock: boolean;
  private hadBlockMultiplier: boolean;
  private enableAnimations: boolean;
  private cellRectangle: Phaser.GameObjects.Rectangle;
  private blockImage: Phaser.GameObjects.Image;
  private blockProjectionImage: Phaser.GameObjects.Image;
  private pointsLabel: Phaser.GameObjects.Text;
  private currentScaleFactor = 1;
  private scaledOffsetX = 0;

  private subscriptionGroup: Subscription;
  private textResizer: TextResizer;
  // private debugContainerVisual: DebugContainerVisual;
  // private debugLabel: Phaser.GameObjects.Text;

  constructor(
    scene: BlocksMainScene,
    x: number,
    y: number,
    private viewModel: CellViewModel,
  ) {
    super(scene, x, y);

    // this.debugContainerVisual = new DebugContainerVisual(scene, this);

    this.subscriptionGroup = new Subscription();
    this.textResizer = new TextResizer(scene, this, {
      fontFamily: 'Inter',
      fontSize: '40px',
      fontStyle: 'bold',
      color: '#ffffff',
    });

    this.hadBlock = false;
    this.hadBlockMultiplier = false;
    this.enableAnimations = false;

    this.cellRectangle = scene.add
      .rectangle(0, 0, 0, 0, viewModel.cellColor)
      .setAlpha(viewModel.cellAlpha);
    this.add([this.cellRectangle]);

    this.blockImage = scene.add.image(0, 0, viewModel.blockNormalAsset.type).setOrigin(0.5, 0.5);
    this.add([this.blockImage]);

    this.blockProjectionImage = scene.add
      .image(0, 0, viewModel.blockNormalAsset.type)
      .setOrigin(0.5, 0.5)
      .setAlpha(0.3);
    this.add([this.blockProjectionImage]);

    this.pointsLabel = scene.add
      .text(0, 0, '', {
        fontFamily: 'Inter',
        fontSize: '40px',
        fontStyle: 'bold',
        color: '#ffffff',
      })
      .setOrigin(0.5, 0.5)
      .setAlpha(0.6);
    this.add([this.pointsLabel]);

    // this.debugLabel = scene.add
    //   .text(0, 0, `${viewModel.id}`, {
    //     fontSize: '18px',
    //     color: '#ffffff',
    //     backgroundColor: '#000000',
    //   })
    //   .setOrigin(0.5, 0.5);
    // this.add([this.debugLabel]);

    this.setName(CellView.name);
    scene.add.existing(this);

    this.subscriptionGroup.add(
      this.viewModel.onGameSetupCompleteTrigger.subscribe(() => {
        this.enableAnimations = true;
      }),
    );
    this.subscriptionGroup.add(
      combineLatest([this.viewModel.blockData$, this.viewModel.isBlockHighlighted$]).subscribe(
        ([blockData, isBlockHighlighted]) => {
          if (blockData?.block) {
            const hasMultiplier = (blockData?.block?.multiplier ?? 0) > 0;

            if (isBlockHighlighted) {
              this.blockImage.setTexture(
                hasMultiplier
                  ? viewModel.blockMultiplierHighlightAsset.type
                  : viewModel.blockNormalHighlightAsset.type,
              );
            } else {
              this.blockImage.setTexture(
                hasMultiplier
                  ? viewModel.blockMultiplierAsset.type
                  : viewModel.blockNormalAsset.type,
              );
            }
          }
        },
      ),
    );
    this.subscriptionGroup.add(
      combineLatest([viewModel.isHighlighted$, viewModel.showProjection$]).subscribe(
        ([isHighlighted, showProjection]) => {
          this.blockProjectionImage.setVisible(isHighlighted || showProjection);
        },
      ),
    );
    this.subscriptionGroup.add(
      this.viewModel.blockData$.subscribe((blockData) => {
        if (this.enableAnimations) {
          if (blockData.block) {
            this.blockImage.setVisible(true);
            this.hadBlock = true;
            this.hadBlockMultiplier = (blockData?.block?.multiplier ?? 0) > 0;
          } else {
            if (this.hadBlock) {
              this.animateDestroyBlock(blockData.displayIndex, this.hadBlockMultiplier);
              this.hadBlock = false;
              this.hadBlockMultiplier = false;
            }
            this.blockImage.setVisible(false);
          }
        } else {
          this.blockImage.setVisible(blockData.block !== null);
          this.hadBlock = blockData.block !== null;
          this.hadBlockMultiplier = (blockData?.block?.multiplier ?? 0) > 0;
        }
      }),
    );
    this.subscriptionGroup.add(
      this.viewModel.points$.subscribe((points) => {
        this.pointsLabel.setVisible(points.amount > 0);
        this.pointsLabel.setText(points.display);
      }),
    );
  }

  override destroy(_fromScene?: boolean | undefined): void {
    this.subscriptionGroup.unsubscribe();

    super.destroy(_fromScene);
  }

  public onResize(gameSizeMetrics: GameSizeMetrics) {
    this.resizeCell(gameSizeMetrics);
  }

  public animateWinCellCollected(displayIndex: number) {
    if (this.hadBlock) {
      this.animateWinBlock(displayIndex);
      this.hadBlock = false;
      this.hadBlockMultiplier = false;
    }
    this.blockImage.setVisible(false);
  }

  private resizeCell(gameSizeMetrics: GameSizeMetrics) {
    const layoutConfig = this.viewModel.layoutConfig;
    const calc = this.viewModel.calculateScaleFactor(gameSizeMetrics);
    this.currentScaleFactor = calc.scaleFactor;
    this.scaledOffsetX = calc.scaledOffsetX;

    const cellSz = (layoutConfig.boardWidth - layoutConfig.cellBorder) / 9;
    const innerCellSz = cellSz - layoutConfig.cellBorder;
    const scaledCellSz = innerCellSz * calc.scaleFactor;

    // Unity has (0,0) bottom left, so flip the row so that it appears correctly
    // const row = 9 - this.viewModel.row;
    // TODO: For now let's keep it like that

    const row = 1 + this.viewModel.row;
    const column = 1 + this.viewModel.column;

    const xPos = (innerCellSz + layoutConfig.cellBorder) * column - innerCellSz * 0.5;
    const yPos = (innerCellSz + layoutConfig.cellBorder) * row - innerCellSz * 0.5;

    this.setPosition(
      (layoutConfig.hMargin + xPos) * calc.scaleFactor + calc.scaledOffsetX,
      (layoutConfig.vMargin + yPos) * calc.scaleFactor,
    );
    this.setSize(scaledCellSz, scaledCellSz);

    this.blockImage.setDisplaySize(scaledCellSz, scaledCellSz);
    this.blockProjectionImage.setDisplaySize(scaledCellSz, scaledCellSz);
    this.cellRectangle.setSize(scaledCellSz, scaledCellSz);

    const pointsLength = this.pointsLabel.text.length;
    const displayText = pointsLength <= 2 ? 'XXX' : this.pointsLabel.text;
    const fontSize = this.textResizer.fitText(displayText, this.width * 0.8, 14);
    this.pointsLabel.setFontSize(fontSize);

    // this.debugLabel.setPosition(
    //   (innerCellSz - cellBorder * 3) * -0.5 * calc.scaleFactor,
    //   (innerCellSz - cellBorder * 3) * -0.5 * calc.scaleFactor,
    // );
  }

  private animateWinBlock(displayIndex: number) {
    const layoutConfig = this.viewModel.layoutConfig;

    const cellSz = (layoutConfig.boardWidth - layoutConfig.cellBorder) / 9;
    const innerCellSz = cellSz - layoutConfig.cellBorder;
    const scaledCellSz = innerCellSz * this.currentScaleFactor;

    const blockDepth = 300 + displayIndex;
    const blockAsset = this.viewModel.blockNormalAsset;
    const blockImage = this.scene.add
      .image(this.x, this.y, blockAsset.type)
      .setOrigin(0.5, 0.5)
      .setDepth(blockDepth);

    blockImage.setDisplaySize(scaledCellSz, scaledCellSz);

    this.scene.tweens.add({
      targets: [blockImage],
      angle: 135,
      alpha: 0.2,
      scale: 0,
      duration: 500,
      ease: 'Quart.In',
      onComplete: () => {
        blockImage.destroy();
      },
    });
    return;
  }

  private animateDestroyBlock(displayIndex: number, isMultiplier: boolean) {
    const layoutConfig = this.viewModel.layoutConfig;

    const cellSz = (layoutConfig.boardWidth - layoutConfig.cellBorder) / 9;
    const innerCellSz = cellSz - layoutConfig.cellBorder;
    const scaledCellSz = innerCellSz * this.currentScaleFactor;

    const blockDepth = (isMultiplier ? 400 : 300) + displayIndex;
    const blockAsset = isMultiplier
      ? this.viewModel.blockMultiplierAsset
      : this.viewModel.blockNormalAsset;
    const blockImage = this.scene.add
      .image(this.x, this.y, blockAsset.type)
      .setOrigin(0.5, 0.5)
      .setDepth(blockDepth);

    blockImage.setDisplaySize(scaledCellSz, scaledCellSz);

    if (!isMultiplier) {
      this.scene.tweens.add({
        targets: [blockImage],
        angle: 135,
        alpha: 0.2,
        scale: 0,
        delay: 50 * displayIndex,
        duration: 500,
        ease: 'Quart.In',
        onComplete: () => {
          blockImage.destroy();
        },
      });
      return;
    }

    const xPos = (innerCellSz + layoutConfig.cellBorder) * 2 - innerCellSz * 0.5;
    const yPos = layoutConfig.controlsHeight * 0.5;

    const target = {
      x: (layoutConfig.hMargin + xPos) * this.currentScaleFactor + this.scaledOffsetX,
      y: (layoutConfig.controlsMargin + yPos) * this.currentScaleFactor,
    };

    const totalTimeMs = 1000;
    const scaleUpTimeMs = totalTimeMs * 0.4;
    const scaleDownTimeMs = totalTimeMs * 0.8;
    const fadeOutTimeMs = totalTimeMs * 0.05;
    const defaultBlockScale = blockImage.scaleX;

    this.scene.tweens.add({
      targets: [blockImage],
      scale: defaultBlockScale * 1.5,
      duration: scaleUpTimeMs,
      ease: 'Quint.Out',
    });

    this.scene.tweens.add({
      targets: [blockImage],
      scale: defaultBlockScale * 0.4,
      angle: 180,
      delay: totalTimeMs - scaleDownTimeMs,
      duration: scaleDownTimeMs,
      ease: 'Quad.In',
    });

    this.scene.tweens.add({
      targets: [blockImage],
      alpha: 0.0,
      delay: totalTimeMs - fadeOutTimeMs,
      duration: fadeOutTimeMs,
      ease: 'Quad.Out',
    });

    this.scene.tweens.add({
      targets: [blockImage],
      x: target.x,
      y: target.y,
      duration: totalTimeMs,
      ease: 'Back.In',
      onComplete: () => {
        blockImage.destroy();
      },
    });
  }
}
